import {
  GROUP_IMAGE_POSITIONS,
  SLIDE_CALLOUT_TONES,
  SLIDE_IMAGE_ALIGNS,
  SLIDE_IMAGE_LAYOUTS,
  SLIDE_IMAGE_SIZES,
  TRAINING_PAGE_TYPES,
  TRAINING_UNMARKED_ONLY_PAGE_TYPES,
  TRAINING_TEXT_INPUT_KINDS,
  TEXT_GROUP_LAYOUTS,
  type TrainingTextInputKind,
  type TextGroupLayout,
  type TrainingFollowUpDef,
  type TrainingTextGroupDef,
  type TrainingTextGroupRowDef,
  type GroupImagePosition,
  type SlideCalloutTone,
  type SlideContentBlock,
  type SlideImageAlign,
  type SlideLeafBlock,
  type TrainingCategoryItem,
  type TrainingGroupRowDef,
  type TrainingHotspotRegion,
  type TrainingMatchPair,
  type TrainingModuleSchema,
  type TrainingOption,
  type TrainingPageDef,
  type TrainingPageType,
  type TrainingQuestionDef,
  type TrainingQuestionGroupDef,
  type TrainingRetryMode,
  type TrainingSchemaValidationResult,
  type TrainingSection,
  type TrainingSlideDef,
} from './types';
import { trainingQuestions, schemaHasQuestionGroup } from './queries';
import { isFollowUpSourceType } from './followUp';

/**
 * Publish-time validation for a module schema. Pure and dependency-free so
 * the builder and the server share one source of truth, and collects every
 * problem rather than failing on the first.
 *
 * The `graded` option is the one axis that changes the rules: an ungraded
 * module (a DSE-style assessment that collects answers rather than testing
 * knowledge) authors no correct answers, so those requirements are dropped
 * and the inherently-graded question types are rejected outright.
 */

/** Options shared by the schema and page validators. */
export interface TrainingValidateOptions {
  /** Defaults to true. False relaxes the correct-answer requirements. */
  graded?: boolean;
}

/** Question types that only make sense when there is a right answer. */
const GRADED_ONLY_TYPES = new Set(['categorize', 'ordering', 'hotspot', 'matching']);

const SLUG_RE = /^[a-z0-9_-]+$/;

/** True if `key` is a valid module slug (used by the create endpoint). */
export function isValidTrainingModuleKey(key: string): boolean {
  return SLUG_RE.test(key);
}

function isNormalised(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1;
}

function nonEmptyText(v: unknown): v is string {
  return typeof v === 'string' && v.trim() !== '';
}

function isPositiveInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1;
}

/**
 * Points only carry meaning when there is a score. A marked module needs a
 * positive weight per question; an unmarked assessment accepts zero so an
 * information-only question is not forced to pretend it is worth something.
 */
function isValidPoints(v: unknown, graded: boolean): v is number {
  return graded ? isPositiveInt(v) : typeof v === 'number' && Number.isInteger(v) && v >= 0;
}

function pointsMessage(graded: boolean): string {
  return graded ? 'points must be a positive whole number' : 'points must be a whole number';
}

function validateOptions(
  options: unknown,
  min: number,
  what: string,
  where: string,
  errors: string[],
): options is TrainingOption[] {
  if (!Array.isArray(options) || options.length < min) {
    errors.push(`${where}: needs at least ${min} ${what}`);
    return false;
  }
  const ids = new Set<string>();
  for (const o of options as Partial<TrainingOption>[]) {
    if (typeof o?.id !== 'string' || o.id === '') {
      errors.push(`${where}: every ${what.replace(/s$/, '')} needs an id`);
      return false;
    }
    if (ids.has(o.id)) {
      errors.push(`${where}: duplicate ${what.replace(/s$/, '')} id "${o.id}"`);
      return false;
    }
    ids.add(o.id);
    if (typeof o.label !== 'string' || o.label.trim() === '') {
      errors.push(`${where}: every ${what.replace(/s$/, '')} needs a label`);
      return false;
    }
  }
  return true;
}

function validateQuestion(
  q: Partial<TrainingQuestionDef>,
  where: string,
  errors: string[],
  graded: boolean,
): void {
  if (typeof q.prompt !== 'string' || q.prompt.trim() === '') {
    errors.push(`${where}: prompt is required`);
  }
  if (!isValidPoints(q.points, graded)) {
    errors.push(`${where}: ${pointsMessage(graded)}`);
  }
  if (!graded && typeof q.type === 'string' && GRADED_ONLY_TYPES.has(q.type)) {
    errors.push(
      `${where}: "${q.type}" questions need a correct answer, so they cannot be used in an assessment that is not marked`,
    );
    return;
  }
  switch (q.type) {
    case 'single_choice': {
      if (validateOptions(q.options, 2, 'options', where, errors) && graded) {
        const ids = q.options.map((o) => o.id);
        if (typeof q.correct_option_id !== 'string' || !ids.includes(q.correct_option_id)) {
          errors.push(`${where}: must mark exactly one existing option as correct`);
        }
      }
      break;
    }
    case 'multi_select': {
      if (validateOptions(q.options, 2, 'options', where, errors) && graded) {
        const ids = new Set(q.options.map((o) => o.id));
        const correct = q.correct_option_ids;
        if (!Array.isArray(correct) || correct.length === 0) {
          errors.push(`${where}: must mark at least one option as correct`);
        } else if (correct.some((id) => !ids.has(id))) {
          errors.push(`${where}: a correct option id does not exist in the options`);
        } else if (new Set(correct).size !== correct.length) {
          // Duplicates would skew the set-size comparison in grading.
          errors.push(`${where}: correct option ids must be unique`);
        }
      }
      break;
    }
    case 'true_false': {
      if (graded && typeof q.correct !== 'boolean') {
        errors.push(`${where}: true/false needs the correct answer marked`);
      }
      break;
    }
    case 'short_text': {
      if (graded) {
        const accepted = q.accepted;
        if (!Array.isArray(accepted) || !accepted.some((a) => nonEmptyText(a))) {
          errors.push(`${where}: needs at least one accepted answer`);
        }
      }
      break;
    }
    case 'categorize': {
      const catIds = new Set<string>();
      if (!Array.isArray(q.categories) || q.categories.length < 2) {
        errors.push(`${where}: needs at least 2 categories`);
      } else {
        for (const c of q.categories as Partial<TrainingOption>[]) {
          if (typeof c?.id !== 'string' || c.id === '' || !nonEmptyText(c.label)) {
            errors.push(`${where}: every category needs a label`);
            break;
          }
          if (catIds.has(c.id)) {
            errors.push(`${where}: duplicate category id "${c.id}"`);
            break;
          }
          catIds.add(c.id);
        }
      }
      if (!Array.isArray(q.items) || q.items.length < 1) {
        errors.push(`${where}: needs at least one item to sort`);
      } else {
        for (const it of q.items as Partial<TrainingCategoryItem>[]) {
          if (!nonEmptyText(it?.label)) {
            errors.push(`${where}: every item needs a label`);
            break;
          }
          if (typeof it.category_id !== 'string' || !catIds.has(it.category_id)) {
            errors.push(`${where}: every item must belong to one of the categories`);
            break;
          }
        }
      }
      break;
    }
    case 'ordering': {
      validateOptions(q.items, 2, 'items', where, errors);
      break;
    }
    case 'hotspot': {
      if (typeof q.image_key !== 'string' || q.image_key === '') {
        errors.push(`${where}: hotspot needs an image`);
      }
      const regions = q.regions;
      if (!Array.isArray(regions) || regions.length === 0) {
        errors.push(`${where}: hotspot needs at least one correct region`);
      } else {
        for (const r of regions as Partial<TrainingHotspotRegion>[]) {
          if (
            !isNormalised(r?.x) ||
            !isNormalised(r?.y) ||
            !isNormalised(r?.w) ||
            !isNormalised(r?.h)
          ) {
            errors.push(`${where}: region coordinates must be between 0 and 1`);
            break;
          }
          if ((r.w as number) <= 0 || (r.h as number) <= 0) {
            errors.push(`${where}: regions need a positive width and height`);
            break;
          }
        }
      }
      break;
    }
    case 'matching': {
      const pairs = q.pairs;
      if (!Array.isArray(pairs) || pairs.length < 2) {
        errors.push(`${where}: matching needs at least 2 pairs`);
      } else {
        const ids = new Set<string>();
        for (const p of pairs as Partial<TrainingMatchPair>[]) {
          if (typeof p?.id !== 'string' || p.id === '') {
            errors.push(`${where}: every pair needs an id`);
            break;
          }
          if (ids.has(p.id)) {
            errors.push(`${where}: duplicate pair id "${p.id}"`);
            break;
          }
          ids.add(p.id);
          if (
            typeof p.left !== 'string' ||
            p.left.trim() === '' ||
            typeof p.right !== 'string' ||
            p.right.trim() === ''
          ) {
            errors.push(`${where}: every pair needs text on both sides`);
            break;
          }
        }
      }
      break;
    }
    default:
      errors.push(`${where}: unknown question type "${String((q as { type?: unknown }).type)}"`);
  }
}

/**
 * A question group: shared answer columns plus one row per question. Row ids
 * key their own answers, so the caller passes the module-wide id set and
 * every row is registered in it.
 */
function validateQuestionGroup(
  group: Partial<TrainingQuestionGroupDef>,
  where: string,
  errors: string[],
  graded: boolean,
  seenIds: Set<string>,
): void {
  if (!validateOptions(group.options, 2, 'answer columns', where, errors)) return;
  const optionIds = new Set(group.options.map((o) => o.id));
  if (!Array.isArray(group.rows) || group.rows.length === 0) {
    errors.push(`${where}: needs at least one question row`);
    return;
  }
  for (const [ri, row] of group.rows.entries()) {
    const rowWhere = `${where} row ${ri + 1}`;
    const r = row as Partial<TrainingGroupRowDef>;
    if (typeof r?.id !== 'string' || r.id === '') {
      errors.push(`${rowWhere}: id is required`);
    } else if (seenIds.has(r.id)) {
      errors.push(`${rowWhere}: duplicate question id "${r.id}"`);
    } else {
      seenIds.add(r.id);
    }
    if (!nonEmptyText(r.prompt)) errors.push(`${rowWhere}: prompt is required`);
    if (!isValidPoints(r.points, graded)) {
      errors.push(`${rowWhere}: ${pointsMessage(graded)}`);
    }
    if (
      graded &&
      (typeof r.correct_option_id !== 'string' || !optionIds.has(r.correct_option_id))
    ) {
      errors.push(`${rowWhere}: must mark one of the answer columns as correct`);
    }
  }
  if (
    group.image_position !== undefined &&
    !GROUP_IMAGE_POSITIONS.includes(group.image_position as GroupImagePosition)
  ) {
    errors.push(`${where}: unknown image position "${String(group.image_position)}"`);
  }
  if (
    group.image_align !== undefined &&
    !SLIDE_IMAGE_ALIGNS.includes(group.image_align as SlideImageAlign)
  ) {
    errors.push(`${where}: unknown image alignment "${String(group.image_align)}"`);
  }
  if (group.image_size !== undefined && !SLIDE_IMAGE_SIZES.includes(group.image_size)) {
    errors.push(`${where}: unknown image size "${String(group.image_size)}"`);
  }
}

/**
 * A text group: one free-text question per row. Row ids key their own
 * answers, so every row is registered in the module-wide id set.
 */
function validateTextGroup(
  group: Partial<TrainingTextGroupDef>,
  where: string,
  errors: string[],
  graded: boolean,
  seenIds: Set<string>,
): void {
  if (group.layout !== undefined && !TEXT_GROUP_LAYOUTS.includes(group.layout as TextGroupLayout)) {
    errors.push(`${where}: unknown layout "${String(group.layout)}"`);
  }
  if (!Array.isArray(group.rows) || group.rows.length === 0) {
    errors.push(`${where}: needs at least one question`);
    return;
  }
  for (const [ri, row] of group.rows.entries()) {
    const rowWhere = `${where} question ${ri + 1}`;
    const r = row as Partial<TrainingTextGroupRowDef>;
    if (typeof r?.id !== 'string' || r.id === '') {
      errors.push(`${rowWhere}: id is required`);
    } else if (seenIds.has(r.id)) {
      errors.push(`${rowWhere}: duplicate question id "${r.id}"`);
    } else {
      seenIds.add(r.id);
    }
    if (!nonEmptyText(r.prompt)) errors.push(`${rowWhere}: prompt is required`);
    if (!isValidPoints(r.points, graded)) {
      errors.push(`${rowWhere}: ${pointsMessage(graded)}`);
    }
    if (
      r.input !== undefined &&
      !TRAINING_TEXT_INPUT_KINDS.includes(r.input as TrainingTextInputKind)
    ) {
      errors.push(`${rowWhere}: unknown answer type "${String(r.input)}"`);
    }
  }
}

/**
 * A follow-up page: needs a label to look for, and any explicit source pages
 * must be choice pages that come before it. `earlierSourceIds` is null when
 * validating a bare page list (no module context), which skips that check.
 */
function validateFollowUp(
  page: Partial<TrainingFollowUpDef>,
  where: string,
  errors: string[],
  earlierSourceIds: Set<string> | null,
): void {
  if (!nonEmptyText(page.match_label)) {
    errors.push(`${where}: needs the answer to look for (e.g. "Yes")`);
  }
  if (page.source_page_ids !== undefined) {
    if (
      !Array.isArray(page.source_page_ids) ||
      page.source_page_ids.some((id) => typeof id !== 'string')
    ) {
      errors.push(`${where}: source pages must be a list of page ids`);
    } else if (earlierSourceIds) {
      for (const id of page.source_page_ids) {
        if (!earlierSourceIds.has(id)) {
          errors.push(`${where}: source page "${id}" is not a choice page before this one`);
        }
      }
    }
  }
}

/**
 * Text groups and follow-ups have no answer key, so a marked module cannot
 * hold them. Returns true (and records the error) when the page is barred.
 */
function rejectUnmarkedOnly(
  type: string,
  where: string,
  errors: string[],
  graded: boolean,
): boolean {
  if (!graded || !(TRAINING_UNMARKED_ONLY_PAGE_TYPES as readonly string[]).includes(type)) {
    return false;
  }
  errors.push(
    `${where}: "${type}" pages collect answers without marking them, so they can only be used in an assessment that is not marked`,
  );
  return true;
}

/** Validate one slide content block; columns recurse with allowColumns=false. */
function validateBlock(
  block: unknown,
  where: string,
  errors: string[],
  allowColumns: boolean,
): void {
  if (typeof block !== 'object' || block === null) {
    errors.push(`${where}: must be an object`);
    return;
  }
  const b = block as Partial<SlideContentBlock>;
  if (typeof b.id !== 'string' || b.id === '') {
    errors.push(`${where}: id is required`);
  }
  switch (b.type) {
    case 'heading':
    case 'text':
    case 'statement':
      if (!nonEmptyText((b as { text?: unknown }).text)) {
        errors.push(`${where}: ${b.type} needs text`);
      }
      return;
    case 'list': {
      const list = b as Partial<Extract<SlideLeafBlock, { type: 'list' }>>;
      if (list.style !== 'bullets' && list.style !== 'numbered') {
        errors.push(`${where}: list style must be bullets or numbered`);
      }
      if (!Array.isArray(list.items) || list.items.filter(nonEmptyText).length === 0) {
        errors.push(`${where}: list needs at least one item`);
      }
      return;
    }
    case 'callout': {
      const callout = b as Partial<Extract<SlideLeafBlock, { type: 'callout' }>>;
      if (!SLIDE_CALLOUT_TONES.includes(callout.tone as SlideCalloutTone)) {
        errors.push(`${where}: unknown callout tone "${String(callout.tone)}"`);
      }
      if (!nonEmptyText(callout.text)) errors.push(`${where}: callout needs text`);
      return;
    }
    case 'image': {
      const image = b as Partial<Extract<SlideLeafBlock, { type: 'image' }>>;
      if (!nonEmptyText(image.image_key)) errors.push(`${where}: image needs an upload`);
      if (
        image.size !== undefined &&
        image.size !== 'full' &&
        !SLIDE_IMAGE_SIZES.includes(image.size)
      ) {
        errors.push(`${where}: unknown image size "${String(image.size)}"`);
      }
      if (image.align !== undefined && !SLIDE_IMAGE_ALIGNS.includes(image.align)) {
        errors.push(`${where}: unknown image alignment "${String(image.align)}"`);
      }
      return;
    }
    case 'quote':
      if (!nonEmptyText((b as { text?: unknown }).text)) {
        errors.push(`${where}: quote needs text`);
      }
      return;
    case 'pdf':
      if (!nonEmptyText((b as { file_key?: unknown }).file_key)) {
        errors.push(`${where}: PDF needs a file`);
      }
      return;
    case 'video':
      if (!nonEmptyText((b as { file_key?: unknown }).file_key)) {
        errors.push(`${where}: video needs a file`);
      }
      return;
    case 'table': {
      const table = b as Partial<Extract<SlideLeafBlock, { type: 'table' }>>;
      if (!Array.isArray(table.header) || table.header.filter(nonEmptyText).length === 0) {
        errors.push(`${where}: table needs at least one column heading`);
        return;
      }
      if (!Array.isArray(table.rows) || table.rows.length === 0) {
        errors.push(`${where}: table needs at least one row`);
        return;
      }
      for (const [ri, row] of table.rows.entries()) {
        if (!Array.isArray(row) || row.length !== table.header.length) {
          errors.push(`${where}: row ${ri + 1} must have ${table.header.length} cells`);
        }
      }
      return;
    }
    case 'divider':
      return;
    case 'columns': {
      if (!allowColumns) {
        errors.push(`${where}: columns cannot be nested inside columns`);
        return;
      }
      const cols = b as Partial<Extract<SlideContentBlock, { type: 'columns' }>>;
      for (const side of ['left', 'right'] as const) {
        const cell = cols[side];
        if (!Array.isArray(cell)) {
          errors.push(`${where}: ${side} column must be a block list`);
          continue;
        }
        for (const [ci, child] of cell.entries()) {
          validateBlock(child, `${where} ${side} block ${ci + 1}`, errors, false);
        }
      }
      if (
        Array.isArray(cols.left) &&
        Array.isArray(cols.right) &&
        cols.left.length === 0 &&
        cols.right.length === 0
      ) {
        errors.push(`${where}: columns need content in at least one side`);
      }
      return;
    }
    default:
      errors.push(`${where}: unknown block type "${String((b as { type?: unknown }).type)}"`);
  }
}

/** Slide-only checks, shared by the schema and bare-page validators. */
function validateSlide(slide: Partial<TrainingSlideDef>, where: string, errors: string[]): void {
  const hasContent =
    (typeof slide.title === 'string' && slide.title.trim() !== '') ||
    (typeof slide.body === 'string' && slide.body.trim() !== '') ||
    (typeof slide.image_key === 'string' && slide.image_key !== '') ||
    (Array.isArray(slide.blocks) && slide.blocks.length > 0);
  if (!hasContent) errors.push(`${where}: slide needs a title, content or image`);
  if (slide.image_layout !== undefined && !SLIDE_IMAGE_LAYOUTS.includes(slide.image_layout)) {
    errors.push(`${where}: unknown image layout "${String(slide.image_layout)}"`);
  }
  if (slide.image_size !== undefined && !SLIDE_IMAGE_SIZES.includes(slide.image_size)) {
    errors.push(`${where}: unknown image size "${String(slide.image_size)}"`);
  }
  if (slide.blocks !== undefined) {
    if (!Array.isArray(slide.blocks)) {
      errors.push(`${where}: blocks must be an array`);
      return;
    }
    for (const [bi, block] of slide.blocks.entries()) {
      validateBlock(block, `${where} block ${bi + 1}`, errors, true);
    }
  }
}

/**
 * Validate a module schema (the JSONB persisted per version). Called on
 * publish; drafts are allowed to be incomplete.
 */
export function validateTrainingModuleSchema(
  schema: unknown,
  opts: TrainingValidateOptions = {},
): TrainingSchemaValidationResult {
  const graded = opts.graded !== false;
  const errors: string[] = [];
  if (typeof schema !== 'object' || schema === null) {
    return { valid: false, errors: ['schema must be an object'] };
  }
  const s = schema as Partial<TrainingModuleSchema>;
  if (!Array.isArray(s.pages) || s.pages.length === 0) {
    errors.push('module needs at least one page');
    return { valid: false, errors };
  }

  // Sections (optional). Build the id set up front so page->section refs and
  // the gated-section-needs-a-question check can both be validated below.
  const sectionIds = new Set<string>();
  const gatedSections = new Set<string>();
  if (s.sections !== undefined) {
    if (!Array.isArray(s.sections)) {
      errors.push('sections must be an array');
    } else {
      for (const [si, section] of s.sections.entries()) {
        const sec = section as Partial<TrainingSection>;
        const where = `section ${si + 1}`;
        if (typeof sec?.id !== 'string' || sec.id === '') {
          errors.push(`${where}: id is required`);
        } else if (sectionIds.has(sec.id)) {
          errors.push(`${where}: duplicate section id "${sec.id}"`);
        } else {
          sectionIds.add(sec.id);
          if (sec.pass_mark !== undefined) {
            if (!isValidPassMark(sec.pass_mark)) {
              errors.push(`${where}: pass mark must be a whole number between 0 and 100`);
            } else {
              gatedSections.add(sec.id);
            }
          }
        }
        if (!nonEmptyText(sec.title)) errors.push(`${where}: needs a title`);
      }
    }
  }
  if (!graded && gatedSections.size > 0) {
    errors.push('section pass marks have no meaning in an assessment that is not marked');
  }

  const seenIds = new Set<string>();
  const sectionQuestionCount = new Map<string, number>();
  // Choice pages seen so far, for follow-up source checks: a follow-up may
  // only look back, never forward.
  const earlierSourceIds = new Set<string>();
  let questionCount = 0;
  for (const [pi, page] of s.pages.entries()) {
    const where = `page ${pi + 1}`;
    if (typeof page !== 'object' || page === null) {
      errors.push(`${where}: must be an object`);
      continue;
    }
    const p = page as Partial<TrainingPageDef>;
    if (typeof p.id !== 'string' || p.id === '') {
      errors.push(`${where}: id is required`);
    } else if (seenIds.has(p.id)) {
      errors.push(`${where}: duplicate page id "${p.id}"`);
    } else {
      seenIds.add(p.id);
    }
    if (typeof p.type !== 'string' || !TRAINING_PAGE_TYPES.includes(p.type as TrainingPageType)) {
      errors.push(`${where}: unknown page type "${String(p.type)}"`);
      continue;
    }
    // A group contributes one question per row; a plain question page one;
    // a slide or a follow-up (derived at run time) none.
    const onPage =
      p.type === 'slide' || p.type === 'follow_up'
        ? 0
        : p.type === 'question_group' || p.type === 'text_group'
          ? ((p as Partial<TrainingQuestionGroupDef | TrainingTextGroupDef>).rows?.length ?? 0)
          : 1;
    if (p.section_id !== undefined) {
      if (typeof p.section_id !== 'string' || !sectionIds.has(p.section_id)) {
        errors.push(`${where}: section "${String(p.section_id)}" does not exist`);
      } else if (onPage > 0) {
        sectionQuestionCount.set(
          p.section_id,
          (sectionQuestionCount.get(p.section_id) ?? 0) + onPage,
        );
      }
    }
    if (p.type === 'slide') {
      validateSlide(p as Partial<TrainingSlideDef>, where, errors);
      continue;
    }
    if (rejectUnmarkedOnly(p.type, where, errors, graded)) continue;
    if (p.type === 'question_group') {
      questionCount += onPage;
      validateQuestionGroup(p as Partial<TrainingQuestionGroupDef>, where, errors, graded, seenIds);
      if (typeof p.id === 'string') earlierSourceIds.add(p.id);
      continue;
    }
    if (p.type === 'text_group') {
      questionCount += onPage;
      validateTextGroup(p as Partial<TrainingTextGroupDef>, where, errors, graded, seenIds);
      continue;
    }
    if (p.type === 'follow_up') {
      validateFollowUp(p as Partial<TrainingFollowUpDef>, where, errors, earlierSourceIds);
      continue;
    }
    questionCount += 1;
    validateQuestion(p as Partial<TrainingQuestionDef>, where, errors, graded);
    if (typeof p.id === 'string' && isFollowUpSourceType(p.type)) earlierSourceIds.add(p.id);
  }

  if (questionCount === 0) {
    errors.push('module needs at least one question');
  }

  // A section with a pass mark but no questions could never be cleared on
  // merit (it would pass vacuously), which is almost always an authoring slip.
  for (const id of gatedSections) {
    if ((sectionQuestionCount.get(id) ?? 0) === 0) {
      const idx = (s.sections ?? []).findIndex((sec) => sec?.id === id);
      errors.push(`section ${idx + 1}: a section with a pass mark needs at least one question`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a bare list of pages, e.g. AI-generated content before it is merged
 * into a module. Unlike validateTrainingModuleSchema this imposes no >= 1-page
 * or >= 1-question rule and ignores sections (the builder owns those) — it only
 * checks each page is a well-formed slide, group or question so the editors and
 * the preview can render it. Reuses the same per-block / per-question rules.
 */
export function validateTrainingPages(
  pages: unknown,
  opts: TrainingValidateOptions = {},
): TrainingSchemaValidationResult {
  const graded = opts.graded !== false;
  const errors: string[] = [];
  if (!Array.isArray(pages)) {
    return { valid: false, errors: ['pages must be an array'] };
  }
  const seenIds = new Set<string>();
  for (const [pi, page] of pages.entries()) {
    const where = `page ${pi + 1}`;
    if (typeof page !== 'object' || page === null) {
      errors.push(`${where}: must be an object`);
      continue;
    }
    const p = page as Partial<TrainingPageDef>;
    if (typeof p.id !== 'string' || p.id === '') {
      errors.push(`${where}: id is required`);
    } else if (seenIds.has(p.id)) {
      errors.push(`${where}: duplicate page id "${p.id}"`);
    } else {
      seenIds.add(p.id);
    }
    if (typeof p.type !== 'string' || !TRAINING_PAGE_TYPES.includes(p.type as TrainingPageType)) {
      errors.push(`${where}: unknown page type "${String(p.type)}"`);
      continue;
    }
    if (p.type === 'slide') {
      validateSlide(p as Partial<TrainingSlideDef>, where, errors);
      continue;
    }
    if (rejectUnmarkedOnly(p.type, where, errors, graded)) continue;
    if (p.type === 'question_group') {
      validateQuestionGroup(p as Partial<TrainingQuestionGroupDef>, where, errors, graded, seenIds);
      continue;
    }
    if (p.type === 'text_group') {
      validateTextGroup(p as Partial<TrainingTextGroupDef>, where, errors, graded, seenIds);
      continue;
    }
    if (p.type === 'follow_up') {
      validateFollowUp(p as Partial<TrainingFollowUpDef>, where, errors, null);
      continue;
    }
    validateQuestion(p as Partial<TrainingQuestionDef>, where, errors, graded);
  }
  return { valid: errors.length === 0, errors };
}

/** Validate a pass mark percentage (publish-time check, shared with the builder). */
export function isValidPassMark(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 100;
}

/**
 * True if `url` is a safe external link for a redirect module: http(s) only,
 * parseable, with a host. Blocks javascript:, data:, and every other scheme so
 * the "Open training" button can never run script or reach a non-web target.
 * Shared by the builder (to gate publish) and the server (to trust the value).
 */
export function isValidExternalUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.trim() === '') return false;
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return false;
  }
  return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && parsed.host !== '';
}

/**
 * Publish-time validation for an external (redirect + self-certify) module.
 * Unlike a quiz it has no pages or questions to grade — it needs a safe URL
 * and a confirmation statement (instructions are optional). The counterpart to
 * validateTrainingModuleSchema for the external kind; shared by builder + server.
 */
export function validateExternalModule(input: {
  external_url?: unknown;
  external_confirmation?: unknown;
}): TrainingSchemaValidationResult {
  const errors: string[] = [];
  if (!isValidExternalUrl(input.external_url)) {
    errors.push('external module needs a valid web link (http or https)');
  }
  if (!nonEmptyText(input.external_confirmation)) {
    errors.push('external module needs a confirmation statement to tick');
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Validate the question-bank size against a schema (publish-time check,
 * shared with the builder). Null serves every question and always passes;
 * otherwise it must be a whole number between 1 and the question count, so a
 * module can never be set to serve more questions than it has.
 */
export function validateServeQuestionCount(
  serveQuestionCount: unknown,
  schema: TrainingModuleSchema,
): TrainingSchemaValidationResult {
  if (serveQuestionCount === null || serveQuestionCount === undefined) {
    return { valid: true, errors: [] };
  }
  if (
    typeof serveQuestionCount !== 'number' ||
    !Number.isInteger(serveQuestionCount) ||
    serveQuestionCount < 1
  ) {
    return { valid: false, errors: ['questions to serve must be a positive whole number'] };
  }
  const total = trainingQuestions(schema).length;
  if (serveQuestionCount > total) {
    return {
      valid: false,
      errors: [
        `questions to serve (${serveQuestionCount}) is more than the ${total} question${
          total === 1 ? '' : 's'
        } in this module`,
      ],
    };
  }
  return { valid: true, errors: [] };
}

/**
 * Settings a question group is incompatible with, checked at publish (and
 * mirrored in the builder so the option is disabled rather than rejected):
 *
 *   - Question banks fill one question slot per page, so a grid of rows
 *     cannot be subset or shuffled coherently. The projection and the grader
 *     both ignore the serve config for such a schema; rejecting it here keeps
 *     the stored settings honest about what actually happens.
 *   - "Try again per question" grades one question at a time, which has no
 *     sensible interaction inside a shared-column grid.
 */
export function validateQuestionGroupSettings(
  schema: TrainingModuleSchema,
  settings: {
    retry_mode?: TrainingRetryMode;
    serve_question_count?: number | null;
    shuffle_questions?: boolean;
  },
): TrainingSchemaValidationResult {
  if (!schemaHasQuestionGroup(schema)) return { valid: true, errors: [] };
  const errors: string[] = [];
  if (settings.retry_mode === 'try_again') {
    errors.push(
      'a module with question grids, Q&A or follow-up pages must use full retake, not try again per question',
    );
  }
  if (settings.serve_question_count != null || settings.shuffle_questions === true) {
    errors.push(
      'question banks (random subset or shuffle) cannot be used with question grids, Q&A or follow-up pages',
    );
  }
  return { valid: errors.length === 0, errors };
}
